/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2019 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import { join, keys } from 'ramda';
import React, { useEffect } from 'react';
import classnames from 'classnames';

import Copy from '@nebula/luna/dist/core/copy';
import {
  Composable,
  HeaderBar,
  HeaderBarButtons
} from '@nebula/luna/dist/core/composable';
import IconTooltip from '@nebula/luna/dist/core/icon-tooltip';
import { defineMessages, injectIntl } from '@nebula/izzy/intl';

import Button from '@react/react-spectrum/Button';
import StatusLight from '@react/react-spectrum/StatusLight';
import Wait from '@react/react-spectrum/Wait';

import BoxIllustration from '../../../images/BoxIllustration.svg';
import loadingEmptyRenderer from '../loading.empty.renderer';
import styles from './styles/index.styl';
import SideRail from './side.rail';
import { useOmega } from '../common/omega';
import useMetrics from '../common/metrics';

const MSG = defineMessages({
  applyOrg: {
    id: 'gemini.session.view.applyOrg',
    defaultMessage: 'Save as Org Default'
  },
  applyUser: {
    id: 'gemini.session.view.applyUser',
    defaultMessage: 'Save as User Default'
  },
  clientStatus: {
    id: 'gemini.session.details.clientStatus',
    defaultMessage: '{count} Clients Connected'
  },
  singleClientStatus: {
    id: 'gemini.session.details.singleClientStatus',
    defaultMessage: '1 Client Connected'
  },
  done: {
    id: 'gemini.session.view.finishConfig',
    defaultMessage: 'Done'
  },
  emptyDescription: {
    id: 'gemini.session.view.notFound.description',
    defaultMessage: 'Try creating a new session.'
  },
  emptyHeading: {
    id: 'gemini.session.view.notFound.header',
    defaultMessage: 'Session Not Found'
  },
  endSession: {
    id: 'gemini.session.view.endSession',
    defaultMessage: 'Close Session'
  },
  openSettings: {
    id: 'gemini.session.view.openSettings',
    defaultMessage: 'Manage Defaults'
  },
  reset: {
    id: 'gemini.session.view.reset',
    defaultMessage: 'Reset'
  },
  sessionDetails: {
    id: 'gemini.session.view.sessionDetails',
    defaultMessage: 'Session Details'
  },
  sessions: {
    id: 'gemini.session.view.sessions',
    defaultMessage: 'Sessions'
  },
  socketDisconnected: {
    id: 'gemini.session.details.socketDisconnected',
    defaultMessage: 'Connecting...'
  },
  versionRequiredInfo: {
    id: 'gemini.session.details.versionRequired',
    defaultMessage: '1.2.x Assurance for Android/iOS required for status updates.'
  }
});

const SessionView = ({
  configureWorkspacesEnabled,
  connectedClients,
  createWidget,
  detailsWidget,
  enableValidationPlugins,
  isConfigure,
  izzy,
  onBreadcrumbClick,
  onConfigurePlugins,
  onEndSessionClick,
  onOpenSettings,
  onSelectView,
  onSessionDetailsClick,
  pluginEndpointReady,
  railOpen,
  selectedView,
  sessionName,
  sessionNotFound,
  settingsWidget,
  socketConnected,
  viewPlugins,
  viewPluginsManagerWidget,
  workspacesWidget,
  ...railProps
}) => {
  const connectedClientsCount = keys(connectedClients).length;
  useOmega('assurance:session view', selectedView, false);
  const metrics = useMetrics();
  useEffect(() => {
    metrics.analytics.trackPage();
  }, []);

  return (
    <div
      className={classnames(
        styles.root,
        railOpen ? styles.contentWithRail : styles.contentFull
      )}
    >
      <SideRail
        railOpen={railOpen}
        isConfigure={isConfigure}
        onConfigurePlugins={onConfigurePlugins}
        onOpenSettings={onOpenSettings}
        onSelectView={(v) => {
          onSelectView(v);
          metrics.analytics.trackPage();
        }}
        selectedView={selectedView}
        viewPlugins={viewPlugins}
        {...railProps}
      />
      <Composable
        className={classnames(
          styles.content,
        )}
      >
        <HeaderBar
          breadcrumbs={[
            { label: izzy(MSG.sessions), value: 'home' },
            { label: sessionName, value: 0 }
          ]}
          breadcrumbsVariant="none"
          className={styles.header}
          onBreadcrumb={onBreadcrumbClick}
        >
          <div className={styles.headerRight}>
            {!sessionNotFound && (socketConnected ? (
              <div className={styles.status}>
                <StatusLight variant={connectedClientsCount ? 'positive' : 'negative'}>
                  {connectedClientsCount === 1
                    ? izzy(MSG.singleClientStatus)
                    : izzy(MSG.clientStatus, { count: connectedClientsCount })}
                </StatusLight>
                <IconTooltip
                  variant="tool"
                  info={connectedClientsCount ? join(',', keys(connectedClients)) : izzy(MSG.versionRequiredInfo)}
                />
              </div>
            ) : (
              <div className={styles.connecting}>
                <Wait size="S" />
                <Copy>{izzy(MSG.socketDisconnected)}</Copy>
              </div>
            ))}
            <HeaderBarButtons className={styles.headerBarButtons}>
              {!sessionNotFound && (
                <React.Fragment>
                  <Button
                    data-test-id="sessionDetailsButton"
                    label={izzy(MSG.sessionDetails)}
                    onClick={onSessionDetailsClick}
                    variant="cta"
                  />
                  {(enableValidationPlugins || configureWorkspacesEnabled) && (
                  <Button
                    data-test-id="settingsButton"
                    label="Settings"
                    onClick={onOpenSettings}
                    variant="secondary"
                  />
                  )}
                </React.Fragment>
              )}
              <Button
                data-test-id="endSessionButton"
                label={izzy(MSG.endSession)}
                onClick={onEndSessionClick}
                variant="secondary"
              />
            </HeaderBarButtons>
          </div>
        </HeaderBar>
        {pluginEndpointReady ? (
          <div className={styles.main}>
            {viewPluginsManagerWidget}
          </div>
        ) : (
          loadingEmptyRenderer(!sessionNotFound,
            {
              description: izzy(MSG.emptyDescription),
              heading: izzy(MSG.emptyHeading),
              illustration: <img src={BoxIllustration} alt="" />
            })()
        )}
      </Composable>
      {createWidget}
      {detailsWidget}
      {!configureWorkspacesEnabled ? settingsWidget : workspacesWidget}
      {isConfigure && (
        <div
          onClick={onConfigurePlugins}
          onKeyPress={onConfigurePlugins}
          className={styles.scrim}
        />
      )}
    </div>
  );
};

SessionView.displayName = 'SessionView';

export default injectIntl(SessionView);
